// ENG-747 Phase A. A newer build existing is not enough to show the pill on its own —
// that would nag on every deploy, including ones seconds apart during a rollout. The
// pill only shows once staleness is actually meaningful, by any one of three measures.

export const STALE_BUILD_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

// Default N per the design brief — "make N a constant with a comment". Chosen as a
// round number with no measured basis yet; revisit against real RUM staleness-tail data
// once the pill has shipped and there is something to tune against.
export const STALE_BUILD_MAX_BUILDS_BEHIND = 4;

export interface StaleBuildPolicyInput {
  builtAt: string | null;
  latestBuildNumber: number | null;
  minSupportedBuildNumber: number | null;
  currentBuildNumber: number | null;
  now: number;
}

/**
 * True when a detected newer build crosses at least one threshold: it has been live
 * more than 24h, this tab is `STALE_BUILD_MAX_BUILDS_BEHIND`+ deploys behind, or a
 * `minSupportedBuildNumber` floor is set and this tab is below it. `now` is passed in
 * (not read via `Date.now()` internally) so this stays a pure, deterministic function —
 * easy to unit test, and reusable if the pill ever needs to re-evaluate on a timer
 * without re-fetching.
 */
export function meetsStaleBuildThreshold({
  builtAt,
  latestBuildNumber,
  minSupportedBuildNumber,
  currentBuildNumber,
  now
}: StaleBuildPolicyInput): boolean {
  if (builtAt) {
    const ageMs = now - Date.parse(builtAt);
    if (Number.isFinite(ageMs) && ageMs > STALE_BUILD_MAX_AGE_MS) return true;
  }

  if (
    latestBuildNumber != null &&
    currentBuildNumber != null &&
    latestBuildNumber - currentBuildNumber >= STALE_BUILD_MAX_BUILDS_BEHIND
  ) {
    return true;
  }

  // Inert in Phase A: version.json always writes minSupportedBuildNumber as null (see
  // vite.config.mts), so this branch is unreachable until a later phase starts setting
  // it. Implemented now, correctly, rather than left as a stub, since a floor build
  // number is meaningless without the comparison it exists for.
  if (
    minSupportedBuildNumber != null &&
    currentBuildNumber != null &&
    currentBuildNumber < minSupportedBuildNumber
  ) {
    return true;
  }

  return false;
}
